// src/components/LoginRegister/Login.jsx
import React, {useEffect, useState} from "react";
import {Link, useNavigate, useLocation} from "react-router-dom";
import {supabase} from "../../supabaseClient";
import ToHomeBtn from "../Shared/ToHomepageBtn";
import "./css/Login.css";
import {toast} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {ToastContainer} from "react-toastify";


// 1) Import the AuthContext to see if user is already logged in / admin
import {useAuth} from "../../contexts/AuthContext";

function Login() {
    const navigate = useNavigate();
    const location = useLocation();

    // 2) We also grab user, role, and loading from AuthContext
    const {user, role, loading: authLoading} = useAuth();

    // Local input states
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    // "from" used to redirect back if we were forced to login
    const from = location.state?.from?.pathname || "/login-and-account";

    // 3) If the user is already logged in (according to AuthContext),
    //    check role => navigate to /admin or /login-and-account
    useEffect(() => {
        if (authLoading) return; // Wait until AuthContext finishes

        if (user) {
            // If there's already a logged-in user
            if (role === "admin") {
                navigate("/admin");
            } else {
                navigate("/login-and-account");
            }
        }
        // otherwise, do nothing (show login form)
    }, [authLoading, user, role, navigate]);

    const handleLoginSubmit = async (event) => {
        event.preventDefault();

        // 4) Sign in the user with email/password
        const {data: logInData, error: logInError} = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (logInError) {
            console.error("Login Error:", logInError.message);
            toast.error("Inloggningen error", {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        // Once they are logged in, the AuthContext should pick up that user automatically
        // But you can also do a direct role check here (if you want to force immediate redirect).
        const userId = logInData?.user?.id;
        if (!userId) {
            toast.error("Inloggningen error: försäkra att uppgifter stämmer", {
                position: "top-right",
                autoClose: 3000,
            });
            navigate(from);
            return;
        }

        const getIpAddess = async () => {
            try {
                const response = await fetch("https://api64.ipify.org?format=json");
                if (response.ok) {
                    const data = await response.json();
                    return data.ip;
                } else {
                    return null;
                }
            } catch (e) {
                console.error("cant get them ip, here's why: " + e);
                return null;
            }
        };
        const ipAddress = (await getIpAddess()) || "unknown address";
        const {data: timeStamp, error: timeStampError} = await supabase.from("user_activity").insert([
            {
                user_id: userId,
                activity_type: "login",
                timestamp: new Date().toISOString(),
                device: "Web",
                ip_address: ipAddress,
            },
        ]);

        if (timeStampError) {
            console.error("Error logging activity:", timeStampError);
        }

        // 5) Optionally fetch the role right here:
        const {data: roleRow, error: roleError} = await supabase.from("auth_user_with_role").select("role").eq("id", userId).single();

        if (roleError) {
            console.error("Error fetching role:", roleError.message);
            navigate(from);
            return;
        }

        if (roleRow?.role === "admin") {
            navigate("/admin");
        } else {
            navigate(from);
        }
    };

    // 6) If AuthContext is still loading, or we are checking session, we can show a fallback
    //    But if user is already logged in, the useEffect above handles the redirect
    //    So let's just not block the form if authLoading is false
    //    We'll show the form by default if user is not logged in
    const showForm = !user && !authLoading;

    return (
        <div className="login-container">
            <div className="login-background-image"></div>
            <ToastContainer />
            <ToHomeBtn />

            {showForm ? (
                <div className="login-card">
                    <h2 className="login-heading">Logga in</h2>
                    <form onSubmit={handleLoginSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="email" className="form-label">
                                E-postadress:
                            </label>
                            <input id="email" type="email" name="email" placeholder="Fyll i e-postadress" required className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">
                                Lösenord:
                            </label>
                            <input
                                id="password"
                                type="password"
                                name="password"
                                placeholder="Fyll i lösenord"
                                required
                                className="form-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        <button type="submit" className="login-button">
                            Logga in
                        </button>
                    </form>

                    <p className="register-paragraph">
                        Har du inget konto? <br />
                        <Link to="/register" className="register-link">
                            Skapa konto här
                        </Link>
                    </p>
                </div>
            ) : (
                <h4>Kontrollerar session...</h4>
            )}
        </div>
    );
}

export default Login;
